/**
 * HTTP
 * @file HTTP请求
 * @author 魏宏裕 <weihongyu12@515cn.com>
 */

import axios from 'axios'
import qs from 'qs'
import { LoadingBar, Message } from 'iview'
import router from '@/router/router'

import { apiUrlHeader } from './api_url_header'

let host = apiUrlHeader()

/**
 * HTTP
 * @example <caption>基本使用</caption>
 * import HTTP from './scripts/http.js'
 * const http = new HTTP()
 * http.get(url, params, msg)
 * http.post(url, params, msg)
 * @example <caption>在Vue中使用</caption>
 * this.$http.get(url, params, msg)
 * this.$http.post(url, params, msg)
 */
class HTTP {
  constructor () {
    this.host = host
    this.timeout = 60000
  }

  /**
   * 抛出异常
   * @param {object} response - 异常信息
   * @param {string} msg - 异常提示文字
   * @private
   */
  _throwError (response, msg) {
    if (response) {
      if (response.status >= 400 && response.status < 500) {
        switch (response.status) {
          case 401: // 没有登录或者token失效
            this._errorMsg(response, msg)
            router.replace({name: 'login'})
            break
          case 403: // 没有权限
            this._errorMsg(response, msg)
            router.replace({name: '403'})
            break
          case 404:
            this._errorMsg(response, msg)
            router.push({name: '404'})
            break
          default:
            this._errorMsg(response, msg)
            break
        }
      } else if (response.status >= 500) {
        this._errorMsg(response, msg)
        // router.push({name: '500'})
      }
    } else {
      Message.error('请求出现错误')
      // router.push({name: '500'})
    }
  }

  /**
   * 弹出错误信息
   * @param {object} response - 异常信息
   * @param {string} msg - 异常提示文本
   * @private
   */
  _errorMsg (response, msg) {
    if (msg) {
      Message.error({
        content: msg,
        duration: 10,
        closable: true
      })
    } else if (response.data.message) {
      if (response.data.message.includes('医保')) {
        Message.error({
          content: response.data.message,
          duration: 0,
          closable: true
        })
      } else {
        Message.error({
          content: response.data.message,
          duration: 0,
          closable: true
        })
      }
    } else if (response.data.ExceptionMessage) {
      Message.error(response.data.ExceptionMessage)
    } else if (response.data.data === null) {
      Message.error('请联系系统管理员')
    } else {
      Message.error(response.toString())
    }
  }

  /**
   * GET
   * @param {string} url - 请求的URL
   * @param {object} params - 请求参数
   * @param {string} [msg] - 抛出异常的提示信息
   * @return {Promise}
   */
  get (url, params, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.get(url, {
        params,
        baseURL: this.host,
        timeout: this.timeout,
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }
  /**
   * POST
   * @param {string} url - 请求的URL
   * @param {object} params - 请求参数
   * @param {string} [msg] - 抛出异常的提示信息
   * @return {Promise}
   */
  post (url, params, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, qs.stringify(params), {
        baseURL: this.host,
        timeout: this.timeout,
        headers: {
          'Authorization': accessToken
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  getJY (url, params, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.get(url, {
        params,
        baseURL: this.host,
        timeout: this.timeout * 3,
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  postJY (url, params, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, qs.stringify(params), {
        baseURL: this.host,
        timeout: this.timeout * 3,
        headers: {
          'Authorization': accessToken
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  postXML (url, params, timeout, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, params, {
        baseURL: this.host,
        timeout: timeout || this.timeout,
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/xml'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        reject(error)
      })
    })
  }

  postJson (url, params, timeout, msg = '') {
    const accessToken = `bearer ${window.localStorage.getItem('access_token')}`
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, params, {
        baseURL: this.host,
        timeout: timeout || this.timeout,
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/json'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        reject(error)
      })
    })
  }
}

export default HTTP
